import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { CommunicationRecallsSortOrderType, CommunicationStateType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RecallsSearchResponseModel } from "@/Communications/Api/Dto/Responses/Recalls/RecallsSearchResponseModel";
import { RecallTableItem } from "@/Communications/Models/Recalls/RecallTableItems";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { RecallSearchSettingsContainer_OptimizedV2, RecallSearchSettingsContainer_OptimizedV2State } from "./RecallSearchSettingsContainer_OptimizedV2";

interface IRecallSearch_OptimizedV2Props {
    shouldReload?: boolean;
    onLoadSuccess?: () => void;

    selectedRecallRowClick?: (recall: RecallTableItem) => void;
    communicationsCount?: () => void;
    showNotesPopup: (recall: RecallTableItem, e: React.MouseEvent) => void;

    savedState: RecallSearch_OptimizedV2State;
    settingSavedState: RecallSearchSettingsContainer_OptimizedV2State;
}

class RecallSearch_OptimizedV2State {
    public recalls: RecallTableItem[];
    public searchSettings: RecallSearchSettingsContainer_OptimizedV2State;
    public isSearchSettingsOpen: boolean;

    public searchText: string;
    public totalQueryRows: number;
    public currentPage: number;
    public totalQueryPages: number;
    public selectedPageNumber: number;

    public sortingColumn: CommunicationRecallsSortOrderType;
    public sortingDirection: SortDirection;
    public isColumnOrderingPopupOpen: boolean;

    public static getDefault(): RecallSearch_OptimizedV2State {
        const state = new RecallSearch_OptimizedV2State();
        state.searchText = "";
        state.totalQueryRows = 0;
        state.totalQueryPages = 0;
        state.currentPage = 0;
        state.selectedPageNumber = 1;
        state.recalls = [];
        state.isSearchSettingsOpen = false;
        state.sortingDirection = SortDirection.ASC;
        state.searchSettings = RecallSearchSettingsContainer_OptimizedV2State.getDefault();

        return state;
    }
}

class RecallSearch_OptimizedV2 extends BaseReactComponent<IRecallSearch_OptimizedV2Props, RecallSearch_OptimizedV2State> {
    private _tableCellClass: string = "text-wrap-control";
    public constructor(props: IRecallSearch_OptimizedV2Props) {
        super(props);
        this.state = props.savedState || RecallSearch_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        this.loadRecalls();

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: IRecallSearch_OptimizedV2Props): void {
        if (prevProps.shouldReload !== this.props.shouldReload && this.props.shouldReload) {
            this.loadRecalls();
        }
    }

    public componentWillUnmount(): void {
        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.RecallSearch,
                state: this.state
            }
        ), 0);
    }

    public render(): JSX.Element {
        const saveOptionKey = "CommunicationsRecallsGridSettings";
        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const columnConfiguration = cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("RecallsLabel")}
                    searchText={this.state.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInRecallsLabel")}
                    onSearch={text => this.mutateState(s => {
                        s.searchText = text;
                        s.selectedPageNumber = 1;
                        s.currentPage = 1;
                    }, () => this.loadRecalls())}
                    onRefreshClick={() => this.loadRecalls()}
                    onSettingsClick={() => this.mutateState(s => s.isSearchSettingsOpen = !this.state.isSearchSettingsOpen)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.state.isSearchSettingsOpen && (
                        <RecallSearchSettingsContainer_OptimizedV2
                            onSaveSettingsCallback={containerSettings => {
                                this.mutateState(
                                    s => {
                                        s.searchSettings = containerSettings;
                                        s.isSearchSettingsOpen = false;
                                        s.selectedPageNumber = 1;
                                    },
                                    () => this.loadRecalls()
                                );
                            }}
                            onCancelClick={() => this.mutateState(s => s.isSearchSettingsOpen = false)}
                            onSettingsButtonClick={() => {
                                this.mutateState(s => {
                                    s.isColumnOrderingPopupOpen = true;
                                    s.isSearchSettingsOpen = false;
                                });
                            }}
                            savedState={() => communicationsStore.communicationsTabSavedState.recallSettingContainerSavedState}
                        />
                    )}
                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_recalls_table"
                            TableDataCy="communication-table"
                            Columns={tableColumns}
                            DataSource={this.state.recalls.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Myle_CommunicationsRecalls_Search_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_CommunicationsRecalls_Search_ColumnSortClick}
                            RowClickFunction={e => {
                                if (this.state.isSearchSettingsOpen) {
                                    this.mutateState(s => s.isSearchSettingsOpen = false);
                                    return;
                                }

                                this.props.selectedRecallRowClick(e);
                            }}
                        />
                    </div>

                    <div className="clearfix" />
                </div>

                <div className={CommunicationsTab_OptimizedV2.footerCSS + " table-pager-footer"}>
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.state.searchSettings.selectedPageSizeOption}
                                TotalRows={this.state.totalQueryRows}
                                CurrentPage={this.state.currentPage}
                                changePageFunction={pageNumber => {
                                    this.mutateState(s => s.selectedPageNumber = pageNumber, () => this.loadRecalls());
                                }}
                            />
                        </div>
                    </div>
                </div>
                {this.state.isColumnOrderingPopupOpen && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfiguration}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        dataCy="recallSearch-columnOrder"
                        closeColumnOrderPopup={() =>
                            this.mutateState(s => s.isColumnOrderingPopupOpen = false)
                        }
                    />
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientsLabel"),
                FieldName: "PatientName",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.Patients,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.Patients)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ResponsibleProvidersLabel"),
                FieldName: "ResponsibleProviderName",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.ResponsibleProviders,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.ResponsibleProviders)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionDLabel"),
                FieldName: "Description",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.Description,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.Description)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DueDateLabel"),
                FieldName: "DueDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.DueDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.DueDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("lblDSQPriority"),
                FieldName: "PriorityLookupId",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.Priority,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.Priority)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StateLabel"),
                FieldName: "State",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationRecallsSortOrderType.State,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.State)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusDLabel"),
                FieldName: "Status",
                IsSortable: true,
                RowClickEnabled: true,
                SortColumn: CommunicationRecallsSortOrderType.Status,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationRecallsSortOrderType.Status)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionsLabel"),
                FieldName: "Notes",
                IsSortable: false,
                WidthPercentageDefault: 5
            })
        ];
    }

    private sortArrayBy(column: CommunicationRecallsSortOrderType): void {
        this.mutateState(
            s => {
                s.sortingColumn = column;
                s.sortingDirection = MyleTableHelper.getNewSortDir(column, s.sortingColumn, s.sortingDirection);
            },
            () => this.loadRecalls()
        );
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<RecallTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "PatientName":
                        return {
                            renderCell: item => (
                                <div title={item.PatientName}>
                                    <HyperLinkRedirectControl target={RedirectTo.Patient} redirectValue={item.PatientId} text={item.PatientName} />
                                </div>
                            )
                        };

                    case "ResponsibleProviderName":
                        return {
                            renderCell: item => (
                                <div title={item.ResponsibleProviderName}>
                                    <HyperLinkRedirectControl
                                        className={this._tableCellClass}
                                        target={RedirectTo.Calendar}
                                        redirectValue={item.DoctorContactId}
                                        text={item.ResponsibleProviderName}
                                        targetPatient={item.PatientId}
                                        subject={item.Description}
                                    />
                                </div>
                            )
                        };

                    case "Description":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                return (
                                    <div title={item.Description}>
                                        <SpanControl IsRed={isRed} IsGreen={isGreen} className={this._tableCellClass} text={item.Description} />
                                    </div>
                                );
                            }
                        };

                    case "DueDate":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                return (
                                    <div title={item.DueDateString}>
                                        <SpanControl IsRed={isRed} IsGreen={isGreen} className={this._tableCellClass} text={item.DueDateString} />
                                    </div>
                                );
                            }
                        };

                    case "PriorityLookupId":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                return (
                                    <div title={item.PriorityDisplayString}>
                                        <SpanControl IsRed={isRed} IsGreen={isGreen} className={this._tableCellClass} text={item.PriorityDisplayString} />
                                    </div>
                                );
                            }
                        };

                    case "State":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                return (
                                    <div title={item.PriorityDisplayString}>
                                        <SpanControl IsRed={isRed} IsGreen={isGreen} className={this._tableCellClass} text={item.RecallCommunicationStatus} />
                                    </div>
                                );
                            }
                        };

                    case "Status":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                return (
                                    <div title={item.PriorityDisplayString}>
                                        <SpanControl IsRed={isRed} IsGreen={isGreen} className={this._tableCellClass} text={item.Status} />
                                    </div>
                                );
                            }
                        };

                    case "Notes":
                        return {
                            renderCell: item => {
                                const color = this.getRowColor(item);
                                const isRed = color === "red";
                                const isGreen = color === "green";

                                let notesCount = "+";
                                const notesArray = item.Model.RecallCommunications;
                                let notesIndicatorCss = "bold-text";

                                if (!isArrayNullOrUndefinedOrEmpty(notesArray)) {
                                    notesCount = notesArray.length.toString();
                                    notesIndicatorCss += " red";
                                }

                                if (isRed) {
                                    if (notesIndicatorCss.indexOf("red") < 0) {
                                        notesIndicatorCss += " red";
                                    }
                                }
                                if (isGreen) {
                                    if (notesIndicatorCss.indexOf("green") < 0) {
                                        notesIndicatorCss += " green";
                                    }
                                }

                                const notesControl = (
                                    <a
                                        key="action1"
                                        aria-expanded="false"
                                        onClick={e => this.props.showNotesPopup(item, e)}
                                        data-dropdown=""
                                    >
                                        [
                                        <span id={`dmt_${item.Model.Id}`} className={notesIndicatorCss}> {notesCount} </span>
                                        ]
                                    </a>
                                );

                                return (
                                    <div title={item.PriorityDisplayString}>
                                        {notesControl}
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getRowColor(data: RecallTableItem): string {
        const result = "";

        if (data.Model.IsCompleted)
            return "green";

        if (moment() > moment(data.DueDate))
            return "red";
        return result;
    }

    private loadRecalls(): void {
        let searchSettings = this.state.searchSettings;
        let matchedClinicians = searchSettings.isAllDeactivatedCliniciansChecked ? searchSettings.clinicians : searchSettings.clinicians.filter(p => !p.isInactive());

        let requestData = {
            PracticianIds: matchedClinicians ? matchedClinicians.filter(x => x.Checked).map(x => x.Id) : [],
            PriorityIds: searchSettings.priorities.filter(x => x.Checked).map(x => x.Id),
            PageNumber: this.state.selectedPageNumber,
            PageSize: searchSettings.selectedPageSizeOption,
            RecallStatus: searchSettings.statuses.filter(x => x.Checked).map(x => x.Id),
            FromDueDate: searchSettings.dateFrom,
            ToDueDate: searchSettings.dateTo,
            SearchString: this.state.searchText,
            OrderByColumn: this.state.sortingColumn,
            OrderByDirection: this.state.sortingDirection,
            OrderByPriorityFirst: searchSettings.orderByPriorityFirst
        };

        Client.Postv2(
            ServerOperations.Server.Myle_SearchRecalls,
            requestData,
            response => {
                const searchResult: RecallsSearchResponseModel = response;

                const result = searchResult.Recalls
                    .map(x => RecallTableItem.FromRecall(x));

                this.mutateState(
                    s => {
                        s.recalls = result;
                        s.totalQueryRows = searchResult.TotalQueryRows;
                        s.currentPage = searchResult.CurrentPage;
                        s.totalQueryPages = searchResult.TotalQueryPages;
                    },
                    () => this.props.onLoadSuccess && this.props.onLoadSuccess()
                );
            }
        );
    }
}

export { RecallSearch_OptimizedV2, RecallSearch_OptimizedV2State };
